import { ChargementImpossible } from '@/components/ChargementImpossible'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { BoutonAppeler } from './BoutonAppeler'
import { useDebounced } from '@/hooks/useDebounced'
import { motion } from 'framer-motion'
import {
  Bot,
  Check,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  MessageSquare,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Search,
  StickyNote,
  User,
  Voicemail as VoicemailIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { useParams } from 'react-router'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  cleFil,
  nomLigne,
  useInbox,
  useInboxLines,
  type InboxFilter,
  type InboxItem,
  type InboxMatch,
} from '@/api/inbox'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { useUIStore } from '@/stores/useUIStore'
import { usePlanRights } from '@/hooks/usePlanRights'
import { FiltresBoite } from './FiltresBoite'

interface Props {
  selectedPhone: string | null
  /** Ligne de la conversation ouverte (null : toutes les lignes de la personne). */
  selectedLine?: string | null
  /** `match`: the conversation was found by the content of an exchange (search). */
  onSelect: (phone: string, line: string | null, match?: InboxMatch) => void
  /** Ouvre le clavier dans le panneau central. */
  onCompose: () => void
  composeActive?: boolean
  darkMode?: boolean
}

function formatTime(iso: string, locale: 'fr' | 'en') {
  try {
    const date = new Date(iso)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    return format(date, isToday ? 'HH:mm' : 'd MMM', {
      locale: locale === 'fr' ? fr : enUS,
    })
  } catch {
    return ''
  }
}

function formatDuration(seconds?: number): string {
  const total = Math.max(0, Math.round(seconds || 0))
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function initialesDe(nom: string): string {
  return nom
    .trim()
    .split(/\s+/)
    .filter((m) => /\p{L}/u.test(m[0] || ''))
    .slice(0, 2)
    .map((m) => m[0].toUpperCase())
    .join('')
}

/**
 * Liste de la Boîte de réception : une ligne par personne, son dernier
 * échange (texto, appel, manqué, vocal), l'heure et les non lus.
 */
export function InboxList({
  selectedPhone,
  selectedLine = null,
  onSelect,
  onCompose,
  composeActive = false,
  darkMode = false,
}: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const toggleListCollapsed = useUIStore((s) => s.toggleContactsListCollapsed)
  const listCollapsed = useUIStore((s) => s.contactsListCollapsed)

  const [filter, setFilter] = useState<InboxFilter>('all')
  const [search, setSearch] = useState('')
  const q = useDebounced(search.trim())

  const lineFilter = useUIStore((s) => s.inboxLineFilter)
  const lignes = useInboxLines()
  // Dans « Toutes », la ligne ne sert qu'à distinguer les conversations d'une
  // même personne : affichée seulement pour elle, sinon nulle part.
  const montrerLigne = (item: InboxItem) => lineFilter === null && lignes.length > 1 && item.other_lines > 0
  const query = useInbox(filter, q, lineFilter)
  const estOuvert = (item: InboxItem) =>
    item.phone === selectedPhone && (selectedLine === null || item.line?.number === selectedLine)
  const items = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  )
  const unreadThreads = query.data?.pages[0]?.unread_threads ?? 0

  const loadMore = () => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage()
  }

  // Filtres d'équipe (plan Team) : grisés avec « Inclus dans Team » sinon.
  const { allowed } = usePlanRights()
  const equipe = allowed('collaboration_equipe')
  // Le plan change (fin d'essai) : on revient à « Tous ».
  useEffect(() => {
    if (!equipe && (filter === 'open' || filter === 'mine' || filter === 'mentions' || filter === 'unassigned' || filter === 'done'))
      setFilter('all')
  }, [equipe, filter])

  // Un filtre demandé d'ailleurs (« Voir » du toast des mentions).
  const filtreDemande = useUIStore((s) => s.inboxFiltreDemande)
  const setFiltreDemande = useUIStore((s) => s.setInboxFiltreDemande)
  useEffect(() => {
    if (!filtreDemande) return
    setFilter(filtreDemande as InboxFilter)
    setFiltreDemande(null)
  }, [filtreDemande, setFiltreDemande])

  // La rangée ne montre que les filtres épinglés par l'utilisateur (serveur),
  // les autres sont dans le menu « ⋯ ». En attendant la réponse : les défauts.
  const labels: Record<InboxFilter, string> = {
    all: t('inbox.filters.all', 'Tous'),
    open: t('inbox.filters.open', 'À traiter'),
    mine: t('inbox.filters.mine', 'À moi'),
    mentions: t('inbox.filters.mentions', 'Mentions'),
    unassigned: t('inbox.filters.unassigned', 'Non attribuées'),
    unread: t('inbox.filters.unread', 'Non lus'),
    unanswered: t('inbox.filters.unanswered', 'Sans réponse'),
    missed: t('inbox.filters.missed', 'Manqués'),
    done: t('inbox.filters.done', 'Terminées'),
  }
  const reglages = query.data?.pages[0]?.filters
  const disponibles: InboxFilter[] =
    reglages?.available ??
    (equipe
      ? ['all', 'open', 'mine', 'mentions', 'unassigned', 'unread', 'unanswered', 'missed', 'done']
      : ['all', 'unread', 'unanswered', 'missed'])
  const epingles: InboxFilter[] =
    reglages?.pinned ?? (equipe ? ['all', 'open', 'done', 'mine', 'mentions', 'unread'] : ['all', 'unread', 'unanswered'])
  const compteurs: Partial<Record<InboxFilter, number>> = {
    unread: unreadThreads,
    mentions: query.data?.pages[0]?.mention_threads ?? 0,
  }

  const nomAffiche = (item: InboxItem) => item.name || formatPhoneDisplay(item.phone)

  const renderAvatar = (item: InboxItem, size: 'sm' | 'md') => {
    const nom = item.name
    const initiales = nom ? initialesDe(nom) : ''
    return (
      <Avatar className={size === 'sm' ? 'w-9 h-9' : 'w-8 h-8'}>
        {item.avatar_url && <AvatarImage src={item.avatar_url} alt={nomAffiche(item)} />}
        <AvatarFallback
          className={cn(
            'text-[11px] font-semibold',
            darkMode ? 'bg-slate-700 text-slate-200' : 'bg-gray-200 text-gray-600',
          )}
        >
          {initiales || (
            <User className={cn('w-4 h-4', darkMode ? 'text-slate-500' : 'text-gray-400')} />
          )}
        </AvatarFallback>
      </Avatar>
    )
  }

  // Mode replié : colonne d'avatars cliquables
  if (listCollapsed) {
    return (
      <div
        className={cn(
          'flex flex-col items-center h-full overflow-hidden py-2 gap-1.5',
          darkMode ? 'bg-slate-950' : 'bg-white',
        )}
      >
        <button
          type="button"
          onClick={toggleListCollapsed}
          title={t('sidebar.expandList', 'Déplier la liste')}
          className={cn(
            'w-9 h-9 rounded-md flex items-center justify-center transition-colors flex-shrink-0',
            darkMode
              ? 'text-slate-400 hover:text-white hover:bg-slate-800'
              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100',
          )}
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
        <BoutonAppeler onClick={onCompose} actif={composeActive} darkMode={darkMode} grand />

        <div className="flex-1 min-h-0 w-full">
          {query.isLoading ? (
            <div className="flex justify-center">
              <Loader2
                className={cn(
                  'h-4 w-4 animate-spin mt-2',
                  darkMode ? 'text-slate-500' : 'text-gray-400',
                )}
              />
            </div>
          ) : (
            <Virtuoso
              className="no-scrollbar"
              data={items}
              endReached={loadMore}
              computeItemKey={(_, item) => cleFil(item)}
              itemContent={(_, item) => (
                <div className="flex justify-center py-0.5">
                  <button
                    type="button"
                    onClick={() => onSelect(item.phone, item.line?.number ?? null, item.match)}
                    title={nomAffiche(item)}
                    className={cn(
                      'relative w-9 h-9 rounded-full flex-shrink-0 transition-all',
                      estOuvert(item) && 'ring-2 ring-indigo-500 ring-offset-1',
                      darkMode && estOuvert(item) && 'ring-offset-slate-950',
                    )}
                  >
                    {renderAvatar(item, 'sm')}
                    {item.mentioned && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 flex items-center justify-center text-white text-[9px] font-bold rounded-full bg-amber-500">
                        @
                      </span>
                    )}
                    {item.unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-0.5 flex items-center justify-center text-white text-[8px] font-bold rounded-full bg-indigo-600">
                        {item.unread > 9 ? '9+' : item.unread}
                      </span>
                    )}
                  </button>
                </div>
              )}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col h-full overflow-hidden',
        darkMode ? 'bg-slate-950' : 'bg-white',
      )}
    >
      {/* En-tête : recherche, clavier, replier, filtres */}
      <div className="flex-shrink-0 px-3 pt-2.5 pb-1.5">
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <Search
              className={cn(
                'absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3',
                darkMode ? 'text-slate-500' : 'text-gray-400',
              )}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('inbox.searchPlaceholder', 'Rechercher un nom, un numéro ou un mot...')}
              className={cn(
                'w-full pl-7 pr-2 h-7 rounded-md text-xs border outline-none transition-colors',
                darkMode
                  ? 'bg-slate-900/60 border-slate-700/50 text-white placeholder:text-slate-500 focus:border-slate-600'
                  : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300',
              )}
            />
          </div>
          <BoutonAppeler onClick={onCompose} actif={composeActive} darkMode={darkMode} />
          <button
            type="button"
            onClick={toggleListCollapsed}
            title={t('sidebar.collapseList', 'Replier la liste')}
            className={cn(
              'hidden md:flex flex-shrink-0 w-7 h-7 rounded-md items-center justify-center transition-colors',
              darkMode
                ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700',
            )}
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </button>
        </div>

        <FiltresBoite
          filter={filter}
          onFilter={setFilter}
          pinned={epingles}
          available={disponibles}
          labels={labels}
          counts={compteurs}
          darkMode={darkMode}
        />
      </div>

      {/* Liste */}
      <div className="flex-1 min-h-0">
        {query.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 && query.isError ? (
          <ChargementImpossible darkMode={darkMode} />
        ) : items.length === 0 ? (
          <div
            className={cn(
              'text-center py-12 px-6',
              darkMode ? 'text-slate-400' : 'text-gray-500',
            )}
          >
            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {q
                ? t('inbox.noResults', 'Aucun résultat')
                : filter === 'mine'
                  ? t('inbox.team.emptyMine', 'Aucune conversation ne vous est attribuée')
                  : filter === 'mentions'
                    ? t('inbox.team.emptyMentions', 'Aucune mention à voir')
                  : filter === 'done'
                    ? t('inbox.team.emptyDone', 'Aucune conversation terminée')
                    : t('inbox.empty', 'Aucune conversation')}
            </p>
          </div>
        ) : (
          <Virtuoso
            data={items}
            endReached={loadMore}
            computeItemKey={(_, item) => cleFil(item)}
            components={{
              Footer: () =>
                query.isFetchingNextPage ? (
                  <div className="flex justify-center py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                ) : null,
            }}
            itemContent={(_, item) => (
              // Une conversation qui remonte (nouveau message) glisse à sa
              // place au lieu de sauter : on voit ce qui a bougé.
              <motion.div layout="position" transition={{ duration: 0.22, ease: 'easeOut' }}>
              <InboxRow
                item={item}
                name={nomAffiche(item)}
                avatar={renderAvatar(item, 'md')}
                selected={estOuvert(item)}
                ligne={montrerLigne(item) && item.line ? nomLigne(item.line, formatPhoneDisplay) : ''}
                onSelect={onSelect}
                darkMode={darkMode}
                time={formatTime(item.last_at, locale as 'fr' | 'en')}
              />
              </motion.div>
            )}
          />
        )}
      </div>
    </div>
  )
}

interface RowProps {
  item: InboxItem
  name: string
  avatar: ReactNode
  selected: boolean
  /** Nom de la ligne, affiché dans « Toutes » ; vide sinon. */
  ligne: string
  onSelect: (phone: string, line: string | null, match?: InboxMatch) => void
  darkMode: boolean
  time: string
}

const ICONES_TROUVAILLE = {
  sms: MessageSquare,
  call: Phone,
  voicemail: VoicemailIcon,
  note: StickyNote,
} as const

/** Search result found in the content: the snippet, searched words highlighted. */
function ExtraitTrouve({ match, darkMode }: { match: InboxMatch; darkMode: boolean }) {
  const { t } = useTranslation()
  const Icon = ICONES_TROUVAILLE[match.kind]
  const libelles = {
    sms: t('inbox.match.sms', 'Texto'),
    call: t('inbox.match.call', 'Appel'),
    voicemail: t('inbox.match.voicemail', 'Message vocal'),
    note: t('inbox.match.note', 'Note'),
  }
  return (
    <>
      <p
        title={match.text}
        className={cn(
          'flex-1 min-w-0 flex items-center gap-1 text-[11px]',
          darkMode ? 'text-slate-400' : 'text-gray-500',
        )}
      >
        <Icon className="w-3 h-3 flex-shrink-0" aria-label={libelles[match.kind]} />
        <span className="truncate">
          {match.parts.map((part, i) =>
            part.hit ? (
              <mark
                key={i}
                className={cn(
                  'rounded-sm px-px font-medium',
                  darkMode ? 'bg-amber-400/25 text-amber-100' : 'bg-amber-100 text-gray-900',
                )}
              >
                {part.text}
              </mark>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        </span>
      </p>
      {match.count > 1 && (
        <span className={cn('flex-shrink-0 text-[10px]', darkMode ? 'text-slate-500' : 'text-gray-400')}>
          {t('inbox.match.count', '{{count}} résultats', { count: match.count })}
        </span>
      )}
    </>
  )
}

function InboxRow({ item, name, avatar, selected, ligne, onSelect, darkMode, time }: RowProps) {
  const { t } = useTranslation()
  const unread = item.unread > 0
  const { kind, text, duration, ai } = item.last

  // Aperçu du dernier échange, selon sa nature.
  let Icon: typeof PhoneIncoming | null = null
  let preview: string
  let missed = false
  switch (kind) {
    case 'missed':
      Icon = PhoneMissed
      preview = t('inbox.preview.missed', 'Appel manqué')
      missed = true
      break
    case 'voicemail':
      Icon = VoicemailIcon
      preview = `${t('inbox.preview.voicemail', 'Message vocal')} · ${formatDuration(duration)}`
      break
    case 'call_in':
    case 'call_out':
      Icon = ai ? Bot : kind === 'call_in' ? PhoneIncoming : PhoneOutgoing
      preview = `${
        ai
          ? t('inbox.preview.aiAgent', 'Agent IA')
          : kind === 'call_in'
            ? t('inbox.preview.inbound', 'Appel entrant')
            : t('inbox.preview.outbound', 'Appel sortant')
      } · ${formatDuration(duration)}`
      break
    default:
      preview = text?.trim() || t('inbox.preview.attachment', 'Pièce jointe')
      if (kind === 'sms_out') preview = t('inbox.preview.you', 'Vous : {{text}}', { text: preview })
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(item.phone, item.line?.number ?? null, item.match)}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 transition-colors',
        selected
          ? darkMode
            ? 'bg-slate-800/70'
            : 'bg-indigo-50/60'
          : darkMode
            ? 'hover:bg-slate-900/50'
            : 'hover:bg-gray-50/60',
      )}
    >
      <div className="flex-shrink-0">{avatar}</div>

      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 min-w-0">
            <span
              className={cn(
                'text-[13px] truncate',
                unread ? 'font-semibold' : 'font-medium',
                darkMode ? 'text-white' : 'text-gray-900',
              )}
            >
              {name}
            </span>
            {ligne && (
              <span
                className={cn(
                  'flex-shrink-0 max-w-[110px] truncate rounded px-1.5 py-px text-[10px] font-medium',
                  darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500',
                )}
              >
                {ligne}
              </span>
            )}
          </span>
          <span className="flex items-center gap-1.5 flex-shrink-0 ml-2">
          {item.mentioned && (
            <span
              title={t('inbox.team.mentionBadge', 'On vous a mentionné')}
              aria-label={t('inbox.team.mentionBadge', 'On vous a mentionné')}
              className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white"
            >
              @
            </span>
          )}
          {item.done && (
            <span
              title={t('inbox.team.doneBadge', 'Terminée')}
              aria-label={t('inbox.team.doneBadge', 'Terminée')}
              className={cn(
                'flex h-4 w-4 items-center justify-center rounded-full',
                darkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-600',
              )}
            >
              <Check className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
          )}
          {item.assigned_to && (
            <span
              title={t('inbox.team.assignedTo', 'Attribuée à {{name}}', { name: item.assigned_to.name })}
              className={cn(
                'flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-semibold',
                darkMode ? 'bg-indigo-500/30 text-indigo-100' : 'bg-indigo-100 text-indigo-700',
              )}
            >
              {item.assigned_to.initials}
            </span>
          )}
          <span
            className={cn(
              'text-[10px]',
              unread
                ? 'text-indigo-600 dark:text-indigo-400 font-semibold'
                : darkMode
                  ? 'text-slate-500'
                  : 'text-gray-400',
            )}
          >
            {time}
          </span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {item.match ? (
            <ExtraitTrouve match={item.match} darkMode={darkMode} />
          ) : (
          <p
            className={cn(
              'flex-1 min-w-0 flex items-center gap-1 text-[11px] truncate',
              missed
                ? 'text-red-500'
                : unread
                  ? darkMode
                    ? 'text-slate-300'
                    : 'text-gray-600'
                  : darkMode
                    ? 'text-slate-500'
                    : 'text-gray-400',
              unread && 'font-medium',
            )}
          >
            {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
            <span className="truncate">{preview}</span>
          </p>
          )}
          {unread && (
            <span className="flex-shrink-0 min-w-4 h-4 px-1 flex items-center justify-center text-white text-[9px] font-bold rounded-full bg-indigo-600">
              {item.unread > 9 ? '9+' : item.unread}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}
